import { isWithinInterval, format, subYears, startOfYear, sub, startOfDay, getUnixTime } from 'date-fns';
import router from '@/router';
import store from '@/store';
import { submitShoppingCart, getProduct, getUserTariffDocuments, removeShoppingCart, getRecurringSessions, getUnpaidInvoices, getCustomerAmplifyUnpaidBookings, getParticipantActiveSubscriptionsInfo } from '@/utils/requests';
import { generateImageUrl, computedOfferPrices } from '@/utils/utils';

const changeToPage = (name, params = {}, query = {}) => {
  store.commit('setFlow', name);

  router.replace({ name: name, params: params, query: query });
};

const confirmParticipants = async ignoreExistingShoppingCart => {
  const { offerInfo, cartItem, siteInfo } = store.state;
  store.commit('setErrorMessage', null);

  const offersField = store.state.newTDA ? 'tariffs' : 'participants';

  const { [offersField]: field } = cartItem;
  let fieldToStore = null;

  if (!store.state.subscriptionTDA && (field || []).some(item => (item.options || []).some(option => option.offerMultiplier))) {
    fieldToStore = (field || []).map(item => {
      const { options } = item;
      /* Daily insurance option quantity gets overridden in API (quantity === sessions length) */
      const optionsToStore = (options || []).map(option => {
        /* fixedQuantity & maxQuantity are not taking into account, they correspond to a single offer unit */
        if (option.offerMultiplier) {
          const optionQuantity = option.quantity * item.quantity;

          return { ...option, quantity: optionQuantity };
        }
        return option;
      });
      return { ...item, options: optionsToStore };
    });

    store.commit('setCartItem', { ...cartItem, [offersField]: fieldToStore });
  }

  if (!(offerInfo.detail.reservation || offerInfo.detail.inscription || (siteInfo.is_internal_session && offerInfo.is_reservation) || (siteInfo.is_internal_session && offerInfo.detail.stage) || (siteInfo.is_internal_session && offerInfo.detail.stage1D)) || cartItem.session) {
    return submitShoppingCart(ignoreExistingShoppingCart)
      .then(response => {
        const cart = response?.addShoppingCartV3;
        if (cart?.success) {
          const { shoppingCart } = cart;
          store.commit('addCartItemToCart', shoppingCart);
          let recapName = 'CartRecap';
          if (store.state.subscriptionTDA) {
            recapName = 'NewTDACartRecapRecurring';
          } else if (store.state.newTDA) {
            recapName = 'NewTDACartRecap';
          }
          changeToPage(recapName);
        } else {
          const message = cart?.message
            || (typeof response?.message === 'string' ? response.message : null)
            || 'Une erreur est survenue lors de l\'ajout au panier.';
          store.commit('setErrorMessage', message);
        }
        return undefined;
      });
  }
  if (store.state.showSessionsStep) {
    changeToPage('NewTDASessions');
  } else {
    changeToPage('Calendar');
  }
  return undefined;
};

const injectCurrentParticipant = async (currentParticipant, ignoreExistingShoppingCart) => {
  // const insuranceOffer = isOfferInsurance();
  // if (insuranceOffer) {
  //   currentParticipant.insuranceInfo = insuranceOffer;
  // }

  if (currentParticipant) {
    const itemToStore = { ...currentParticipant };

    if (itemToStore.period) {
      delete itemToStore.period.__typename;
      if (itemToStore.period.renewedWith) {
        delete itemToStore.period.renewedWith.__typename;
      }
    }

    if (store.getters.isCartContractRecharge) {
      itemToStore.contractToRecharge = store.state.contractToRecharge;
      itemToStore.contractRechargePurchase = true;
    }

    store.commit('setCartItemParticipants', itemToStore);
  }

  if (!(store.getters.isRaquettes || store.getters.isEscapeGame || store.state.newTDA || store.getters.isCartContractRecharge)) {
    store.commit('setCurrentParticipant', null);
    changeToPage('Participants');
  } else {
    await confirmParticipants(ignoreExistingShoppingCart);
  }

  return true;
};

const handleOptionFlow = async () => {
  const options = store.state.availableOptions;
  const { detail, insuranceType } = store.state.offerInfo;
  const { isImpersonateMode } = store.getters;
  // No required documents to upload bue have options to select

  // const isOptionInsurance = options.some(o => {
  //   return  o.detail.insurance
  // })
  // if (options && options.length && isOptionInsurance) {
  //   const response = await getParticipantInsurance(store.state.currentParticipant.uuid);

  //   if (response.insurances && response.insurances.length){
  //     const annualInsurance = response.insurances.filter(insurance => insurance.data.type === 'ANNUAL')
  //     options = options.filter(option => {

  //       if (!option.detail.insurance) {
  //         return true
  //       }
  //       const isOptionSameAsCustomerInsurance = annualInsurance.some(insurance => insurance.data.productUuid === option.product_uuid)
  //       return !isOptionSameAsCustomerInsurance;
  //     })
  //   }
  // }

  if (options && options.length) {
    return changeToPage(`${store.state.newTDA ? 'NewTDA' : ''}Options`);
  }

  const participantToInject = { ...store.state.currentParticipant, quantity: 1 };

  if (detail.insurance && isImpersonateMode) {
    participantToInject.insuranceDetail = { insuranceType };
  }

  // handle offer insurance
  return injectCurrentParticipant(participantToInject);
};

const getRequiredDocuments = async (offer, currentParticipant) => {
  const params = {
    workspace: offer.workspace,
    participant: currentParticipant.uuid,
    code_category_tarrif: currentParticipant?.tariff?.tariffCategoryCode || ''
  };

  return getUserTariffDocuments(params).then(async res => {
    currentParticipant.tariff.documents = res;

    store.commit('setCurrentParticipant', currentParticipant);

    // Skip UploadDocuments step
    if (store.state.newTDA || (currentParticipant.tariff.documents.validInfo && !currentParticipant.tariff.documents.required_documents)) {
      return handleOptionFlow();
    } // Upload required documents
    // Upload Documents
    changeToPage('Documents');
    return true;
  }).catch(err => {
    console.log('Error getting required documents', err);
    return false;
  });
};

const setOptions = async tarif => {
  const { options } = store.state.offerInfo;
  const { siteInfo } = store.state;

  let optionsToShow = [];

  let availableOptions = (options || []).filter(option => {
    if (!option.detail) {
      return false;
    }
    if (siteInfo.is_internal_session) {
      // get the option for the same period
      const optionWithSamePeriod = option.detail.prices.find(p => p.period.uuid === tarif.period.uuid && p.priceCategory.uuid === tarif.priceCategory.uuid);
      if (optionWithSamePeriod) {
        const productOptionOffer = option.product.dynamo_offers.find(o => o.detail.prices.find(p => p.uuid === optionWithSamePeriod.uuid));
        const insuranceOption = option.product.dynamo_offers.find(offer => offer.offerType === 'insurance' && offer.detail.prices.some(price => price.uuid === optionWithSamePeriod.uuid));
        optionsToShow.push({ ...option, price: optionWithSamePeriod, stock: productOptionOffer?.stock, detail: { ...option.detail, insurance: !!insuranceOption, insuranceDetail: insuranceOption ? { insuranceType: insuranceOption.insuranceType } : null } });
      }
      return false;
    }
    const optionHasPriceInCategory = option.detail.prices.find(price => price.category === tarif.category);
    if (optionHasPriceInCategory) {
      option.price = optionHasPriceInCategory;
    }
    return !!optionHasPriceInCategory;
  });

  if (siteInfo.is_internal_session) {
    // const isOptionInsurance = optionsToShow.some(o => {
    //   return o.detail.insurance
    // })

    // // IF OPTION INSURANCE VERIFY IF CUSTOMER ALREADY HAVE ONE
    // if (isOptionInsurance) {
    //   const response = await getParticipantInsurance(store.state.currentParticipant.uuid);

    //   const annualInsurance = response.insurances.filter(insurance => insurance.data.type === 'ANNUAL')

    //   optionsToShow = optionsToShow.filter(option => {
    //     if (!option.detail.insurance) {
    //       return true
    //     }
    //     const isOptionSameAsCustomerInsurance = annualInsurance.some(insurance => {
    //       return insurance.data.productUuid === option.product.uuid
    //     })

    //     return !isOptionSameAsCustomerInsurance;
    //   })
    // }

    if (store.state.singlePurchaseOptions?.length) {
      optionsToShow = (optionsToShow || []).filter(option => (!store.state.singlePurchaseOptions.some(singlePurchaseOption => singlePurchaseOption?.productUuid === option?.product?.uuid && singlePurchaseOption?.periodUuid === tarif?.period?.uuid && singlePurchaseOption?.customerUuid === store.state.currentParticipant?.uuid)));
    }

    availableOptions = optionsToShow.map(option => ({
      uuid: option.price.uuid,
      product_uuid: option.product.uuid,
      name: option.name,
      description: option.description,
      cancelation: option.cancelation,
      registrationFees: option.registrationFees,
      hasMaxQuantity: option.hasMaxQuantity,
      maxQuantity: option.maxQuantity,
      quantity: option.quantity,
      fixedQuantity: option.fixedQuantity,
      optionType: option.optionType,
      stock: option.stock,
      detail: option.detail,
      offerMultiplier: option.offerMultiplier,
      price: {
        price_uuid: option.price.uuid,
        category: option.price.priceCategory.name,
        promotions: [],
        price: option.price.price,
        tva: option.price.tva,
        period: option.price.period,
        tcategory: option.price.priceCategory.name,
        tariffCategoryCode: '',
        label: option.price.priceCategory.name,
        conditions: null
      }
    }));
  }
  store.commit('setAvailableOptions', availableOptions);
};

const computeSinglePurchaseOptionsToRemove = offerInfo => {
  const optionsToVerify = (offerInfo?.options || []).filter(option => option.singlePurchasePerPeriod);
  const validCustomerOrders = (store.state.user?.orders || []).filter(order => {
    const now = new Date();
    const startPastYear = startOfYear(subYears(now, 1));
    return isWithinInterval(new Date(parseFloat(order.date)), { start: startPastYear, end: now }) && order.isPaid && order.site_workspace && store.state.siteInfo?.workspace === order.site_workspace;
  });

  if (!validCustomerOrders.length) {
    return;
  }
  const singlePurchaseOptions = [];
  optionsToVerify.map(option => {
    validCustomerOrders.map(orderGeneric => {
      (orderGeneric.order?.order_items || []).map(item => {
        const orderOption = (item.options || []).find(op => op.product_uuid === option.product.uuid);
        if (orderOption && orderOption.period?.uuid) {
          singlePurchaseOptions.push({ customerUuid: item.customer.uuid, productUuid: option.product.uuid, periodUuid: orderOption.period.uuid });
        }
      });
    });
  });
  store.commit('setSinglePurchaseOptions', [...singlePurchaseOptions]);
};

const handleOfferInfo = (product, offerCode) => {
  if (product.dynamo_offers && product.dynamo_offers.length) {
    product.dynamo_offers.map(offer => {
      if (offer.detail) {
        if (offer.detail.lowest_price_promo || offer.detail.lowest_price_promo === 0) {
          const lowest_price_promo = parseInt(offer.detail.lowest_price_promo);
          offer.detail.lowest_price_promo = parseFloat(lowest_price_promo / 100).toFixed(2);
        }
        const lowest_price = parseInt(offer.detail.lowest_price);
        offer.detail.lowest_price = parseFloat(lowest_price / 100).toFixed(2);
        offer.detail.prices = offer.detail.prices.map(price => {
          const selectedHoranetTariff = (offer?.selectedHoranetTariffs || []).find(t => t.codeTariff === price.tariffCode);
          if (selectedHoranetTariff) {
            return { ...price, ...selectedHoranetTariff };
          }
          return price;
        });
      }
      return offer;
    });
  }

  let offerInfo = null;

  if (store.state.siteInfo.is_internal_session) {
    offerInfo = (product.dynamo_offers || []).find(offer => offer.uuid === offerCode);
  } else {
    offerInfo = (product.dynamo_offers || []).find(offer => offer.horanet_product.horanet_product_code === parseInt(offerCode));
  }

  let isDisabled = false;

  if (!offerInfo) {
    isDisabled = true;
  } else {
    const isOfferWithoutSessions = ((offerInfo.detail.reservation || offerInfo.detail.inscription) && !offerInfo.detail.haveSessions);
    isDisabled = ['VENTE_SUR_PLACE', 'SOON_AVAILABLE'].includes(product.sales_status) || isOfferWithoutSessions;
  }

  if (isDisabled) {
    store.commit('setOfferInfo', null);
    return false;
  }

  let isNewTDA = false;
  let isSubscriptionTDA = false;
  let isCoupeFileTDA = false;

  if (offerInfo && offerInfo.detail) {
    if (offerInfo.expressCheckout) {
      isCoupeFileTDA = true;
    }
    if (offerInfo.detail.recurring) {
      isSubscriptionTDA = true;
      isNewTDA = true;
    }
    if (offerInfo.detail.unit_sales && !offerInfo.detail.pass) {
      isNewTDA = true;
    }
    const { workspace } = store.getters;

    if (['STAGE'].includes(offerInfo.offer_type) && ['alpha_ed', 'alpha_formations'].includes(workspace) && offerInfo.detail.inscription) {
      if (offerInfo.detail.prices.length === 1) {
        const price = offerInfo.detail.prices[0];
        const options = (offerInfo.options || []).filter(option => option.detail.prices.some(p => p.tariffCategoryCode === price.tariffCategoryCode));
        const mandatoryOptions = options.filter(option => ['MANDATORY'].includes(option.optionType));

        if (!options.length || options.length === mandatoryOptions.length) {
          offerInfo.detail.prices[0].options = options.map(o => ({
            ...o,
            quantity: 1
          }));
          store.commit('setShowTariffsStep', false);
        }
      }

      store.commit('setShowParticipantStep', true);
      store.commit('setShowSessionsStep', true);
      store.commit('setCurrentParticipant', null);

      isNewTDA = true;
    } else if (isSubscriptionTDA) {
      const isInternalSession = store.state.siteInfo.is_internal_session;
      offerInfo.detail.prices.forEach(price => {
        price.options = (offerInfo.options || []).filter(option => (option?.detail?.prices || []).some(p => {
          if (isInternalSession) {
            return p.priceCategory.uuid === price.priceCategory.uuid;
          }
          return p.tariffCategoryCode === price.tariffCategoryCode;
        }));
        const mandatoryOptions = price.options.filter(option => ['MANDATORY'].includes(option.optionType));
        if (mandatoryOptions.length) {
          price.options.forEach(option => {
            const isMandatoryOptions = mandatoryOptions.some(mo => {
              if (isInternalSession) {
                return mo.product.uuid === option.product.uuid;
              }
              return mo.horanet_product.horanet_product_code === option.horanet_product.horanet_product_code;
            });
            if (isMandatoryOptions) {
              option.quantity = 1;
            }
          });
        }
      });
    }
  }
  if (store.state.siteInfo?.is_internal_session && store.state.user?.orders?.length && (offerInfo?.options || []).some(option => option.singlePurchasePerPeriod)) {
    computeSinglePurchaseOptionsToRemove(offerInfo);
  }

  delete product.dynamo_offers;

  store.commit('setNewTDA', isNewTDA);
  store.commit('setSubscriptionTDA', isSubscriptionTDA);
  store.commit('expressCheckout/setActive', isCoupeFileTDA);
  store.commit('setProduct', product);
  store.commit('setOfferInfo', offerInfo);
  return true;
};
const handleEscapeProduct = async selectedProduct => getProduct({ product: selectedProduct }).then(async product => {
  const { products, sessionsCodes, user } = store.state;
  const activities = [];
  product.dynamo_offers = product.dynamo_offers.map(offer => {
    if (offer.detail) {
      const lowest_price = parseInt(offer.detail.lowest_price);
      offer.detail.lowest_price = parseFloat(lowest_price / 100).toFixed(2);
      product.lowest_price = parseFloat(lowest_price / 100).toFixed(2);
      if (offer.detail.activities && offer.detail.activities.length) {
        offer.detail.activities.map(activity => {
          if (!activities.some(act => act.activity_code === activity.activity_code)) {
            activities.push(activity);
          }
        });
      }
    }
    offer.isDisabled = false;

    if (!offer) {
      offer.isDisabled = true;
    } else {
      const isOfferWithoutSessions = ((offer.detail.reservation || offer.detail.inscription) && !offer.detail.haveSessions);
      offer.isDisabled = ['VENTE_SUR_PLACE', 'SOON_AVAILABLE'].includes(product.sales_status) || isOfferWithoutSessions;
    }

    return offer;
  });

  product.dynamo_offers = product.dynamo_offers.sort((a, b) => {
    if (a.lowest_price && b.lowest_price) {
      return a.lowest_price - b.lowest_price;
    }
    if (!a.lowest_price) {
      return b.lowest_price;
    }
    if (!b.lowest_price) {
      return a.lowest_price;
    }
    return 0;
  });

  const productAux = products.filter(
    p => p.uuid === selectedProduct
  );
  let sessions = productAux[0].sessions.filter(
    s => sessionsCodes.includes(s.codeCreneau)
  );
  sessions = sessions.map(s => {
    const sessionActivities = (activities || []).filter(act => parseInt(act.activity_code) === s.codeActivite);
    return {
      ...s,
      activities: sessionActivities
    };
  });
  let productImage = null;
  if (product.header_images && product.header_images.length) {
    productImage = generateImageUrl(product.header_images[0], 't_UCPA_Vertical');
  } else if (product.headerImagesSEO && product.headerImagesSEO.length && product.headerImagesSEO[0].url) {
    productImage = generateImageUrl(product.headerImagesSEO[0].url, 't_UCPA_Vertical');
  }

  const cartItem = {
    workspace: product.workspace,
    participants: [user],
    details: {
      product_image:
        productImage,
      product_name: product.name,
      product_uuid: product.uuid
    }
  };

  store.commit('setProduct', product);
  store.commit('setCartItem', { ...cartItem });
  store.commit('setSessions', sessions);
  return true;
}).catch(err => {
  console.log('Error handling escape game product', err);
  return false;
});

const handleProduct = async (selectedProduct, selectedOffer) => getProduct({ product: selectedProduct }).then(async product => {
  const { products, sessionsCodes, siteInfo } = store.state;

  const offerInfo = product.dynamo_offers.find(offer => {
    if (siteInfo.is_internal_session) {
      return offer.uuid === selectedOffer;
    }
    return offer.horanet_product.horanet_product_code === parseInt(selectedOffer);
  });

  const handleOfferInfoResponse = handleOfferInfo(product, selectedOffer);

  if (!handleOfferInfoResponse) return false;

  const productAux = products.filter(p => p.uuid === selectedProduct);

  let sessions = productAux[0].sessions.filter(s => {
    if (siteInfo.is_internal_session) {
      return s.product_activity.some(pa => pa.product === selectedProduct) && sessionsCodes.includes(s.codeCreneau);
    }
    return s.CodeProduit === selectedOffer && sessionsCodes.includes(s.codeCreneau);
  });

  sessions = sessions.map(s => {
    const sessionActivities = (offerInfo.detail?.activities || []).filter(act => {
      if (siteInfo.is_internal_session) {
        return s.product_activity.some(pa => pa.activity === act.uuid);
      }
      return parseInt(act.activity_code) === s.codeActivite;
    });

    return {
      ...s,
      activities: sessionActivities
    };
  });

  let productImage = null;
  if (product.header_images && product.header_images.length) {
    productImage = generateImageUrl(product.header_images[0], 't_UCPA_Vertical');
  } else if (product.headerImagesSEO && product.headerImagesSEO.length && product.headerImagesSEO[0].url) {
    productImage = generateImageUrl(product.headerImagesSEO[0].url, 't_UCPA_Vertical');
  }

  const cartItem = {
    workspace: product.workspace,
    participants: [],
    details: {
      offerUuid: offerInfo.uuid,
      stock: offerInfo.stock,
      sans_support: offerInfo.sans_support,
      contremarque: offerInfo.contremarque,
      tda_info: offerInfo.tda_info,
      first_purchase: offerInfo.detail.first_purchase,
      horanet_product_name: siteInfo && siteInfo.is_internal_session ? offerInfo.name : offerInfo.horanet_product.name,
      horanet_product_code: siteInfo && siteInfo.is_internal_session ? offerInfo.uuid : offerInfo.horanet_product.horanet_product_code,
      horanet_product_reference: siteInfo && siteInfo.is_internal_session ? offerInfo.uuid : offerInfo.horanet_product.horanet_product_reference,
      inscription: offerInfo.detail.inscription,
      pass: offerInfo.detail.pass,
      product_image:
        productImage,
      recurring: offerInfo.detail.recurring,
      stage: offerInfo.detail.stage,
      stage1D: offerInfo.detail.stage1D,
      license: offerInfo.detail.license,
      reloadable: offerInfo.detail.reloadable,
      reservation: offerInfo.detail.reservation,
      unit_sales: offerInfo.detail.unit_sales,
      product_name: product.name,
      product_uuid: product.uuid,
      product_type: offerInfo.name,
      cancelation: offerInfo.cancelation,
      offer_type: offerInfo.offer_type,
      offerProductType: offerInfo.productType,
      isClubCard: offerInfo.detail.isClubCard,
      firstRecurringSession: offerInfo.firstRecurringSession,
      isLoyaltyOffer: offerInfo.isLoyaltyOffer,
      loyaltyDuration: offerInfo.loyaltyDuration
    }
  };

  store.commit('setCartItem', { ...cartItem });
  store.commit('setSessions', sessions);
  return true;
}).catch(err => {
  console.log('Error handling product', err);
  return false;
});

const handleTariff = async selectedTariff => {
  const { currentParticipant, offerInfo, siteInfo, order, user } = store.state;

  const offerPrices = offerInfo && offerInfo.detail ? computedOfferPrices(currentParticipant) : [];

  const selectedPrice = offerPrices.find(p => p.index === selectedTariff);

  currentParticipant.tariff = JSON.parse(JSON.stringify(selectedPrice));
  currentParticipant.tariff.gymlibCode = currentParticipant.gymlibCode ? JSON.parse(JSON.stringify(currentParticipant.gymlibCode)) : null;

  delete currentParticipant.gymlibCode;

  let skipOptions = store.getters.isCartContractRecharge;

  currentParticipant.period = siteInfo.is_internal_session ? selectedPrice.period : null;
  if (order) {
    const orderPeriod = order.order.order_items[0].period;

    if (user.contacts.some(contact => contact.uuid === order.buyer) && orderPeriod && currentParticipant.period) {
      const orderPrice = offerPrices.find(price => price.period.uuid === orderPeriod.uuid);

      if (orderPrice && orderPrice.renewedWith && orderPrice.renewedWith.uuid === currentParticipant.period.uuid) {
        skipOptions = true;
      }
    }
  }

  if (!skipOptions) {
    await setOptions(currentParticipant.tariff);
  }

  // return getRequiredDocuments(offerInfo, currentParticipant);
  return handleOptionFlow();
};

const hasProductsStep = () => store.state.products && (store.state.products.length > 1 || (store.state.products[0]?.dynamo_offers?.length > 1));

const hasTerrainsStep = () => store.state.sessions && store.state.sessions.items && store.state.sessions.items.length > 1;
const initializeCartItem = ({ participants = [], offer, product, callback, resumeOrder = false, configure = false }) => {
  const { cartItem, siteInfo, currentParticipant } = store.state;
  let productImage = null;
  if (product.header_images && product.header_images.length) {
    productImage = generateImageUrl(product.header_images[0], 't_UCPA_Vertical');
  } else if (product.headerImagesSEO && product.headerImagesSEO.length && product.headerImagesSEO[0].url) {
    productImage = generateImageUrl(product.headerImagesSEO[0].url, 't_UCPA_Vertical');
  }
  if (!currentParticipant && participants[0]) {
    store.commit('setCurrentParticipant', participants[0]);
  }
  if (!cartItem || resumeOrder || configure) {
    store.commit('setCartItem', {
      workspace: product.workspace,
      participants: participants,
      details: {
        offerUuid: offer.uuid,
        stock: offer.stock,
        contremarque: offer.contremarque,
        sans_support: offer.sans_support,
        tda_info: offer.tda_info,
        first_purchase: offer.detail.first_purchase,
        horanet_product_name: !siteInfo.is_internal_session ? offer.horanet_product.name : offer.name,
        horanet_product_code: !siteInfo.is_internal_session ? offer.horanet_product.horanet_product_code : offer.uuid,
        horanet_product_reference: !siteInfo.is_internal_session ? offer.horanet_product.horanet_product_reference : null,
        inscription: offer.detail.inscription,
        pass: offer.detail.pass,
        product_image: productImage,
        productCategory: offer.productCategory,
        recurring: offer.detail.recurring,
        reloadable: offer.detail.reloadable,
        reservation: offer.detail.reservation,
        unit_sales: offer.detail.unit_sales,
        insurance: offer.detail.insurance,
        stage: offer.detail.stage,
        stage1D: offer.detail.stage1D,
        license: offer.detail.license,
        product_name: product.name,
        product_uuid: product.uuid,
        product_type: offer.name,
        cancelation: offer.cancelation,
        offer_type: offer.offer_type,
        offerProductType: offer.productType,
        activities: (offer.detail.activities || []).map(act => act.uuid),
        isPension: offer.isPension,
        isClubCard: offer.detail.isClubCard,
        firstRecurringSession: offer.firstRecurringSession,
        isLoyaltyOffer: offer.isLoyaltyOffer,
        loyaltyDuration: offer.loyaltyDuration
      }
    });
    callback(cartItem);
  }
};

const computeAvailableOptions = (options, tariff) => {
  const options_aux = JSON.parse(JSON.stringify(options)) || [];
  return options_aux.map(option => {
    if (option.detail) {
      const optionHasPriceInCategory = option.detail.prices.find(price => price.tariffCategoryCode === tariff.tariffCategoryCode);

      if (optionHasPriceInCategory) {
        option.price = optionHasPriceInCategory;
      }
    }
    return option;
  });
};

const computeTariffOptions = (availableOptions, tariff) => {
  const computedOptions = [];
  const availableOptions_aux = JSON.parse(JSON.stringify(availableOptions)) || [];
  availableOptions_aux.map(option => {
    const price = option.detail.prices.find(priceAux => priceAux.tariffCategoryCode === tariff.tariffCategoryCode);
    if (price) {
      if (price.promotions && price.promotions.length) {
        option.promo = {};

        price.promotions.map(promo => {
          const startDate = new Date(promo.start_date);
          const endDate = new Date(promo.end_date);
          const now = new Date();
          let day_week = format(now, 'e');

          if (day_week === 1) {
            day_week = 7;
          } else {
            day_week -= 1;
          }

          const isBetween = isWithinInterval(now, {
            start: startDate,
            end: endDate
          });

          if (isBetween /* && promo.quantity_remaining > 0 */ && (promo.week_day === 0 || promo.week_day === day_week)) { // TODO uncomment when correctly receiving quantity remaining
            option.price = promo.price.toFixed(2);
            option.promo = promo;
          }
          return promo;
        });
      }
      computedOptions.push({
        ...option,
        price
      });
    }
    return null; // ESLint need to desativate array-callback-return rule
  });

  return computedOptions;
};

const computePromo = offer => {
  offer.promo = {};
  if (!offer.promotions) {
    return { canShow: false };
  }
  let price = null;
  offer.promotions.map(promo => {
    // offer.promo = {}; // remove later
    const startDate = new Date(promo.start_date);
    const endDate = new Date(promo.end_date);
    const now = new Date();
    let day_week = format(now, 'e');
    /*
      day -> date-fns ->horanet
      Monday -> 2 -> 1
      Tuesday -> 3 -> 2
      Wednesday -> 4 -> 3
      Thursday -> 5 -> 4
      Friday -> 6 -> 5
      Saturday -> 7 -> 6
      Sunday -> 1 -> 7
     */
    if (day_week === 1) {
      day_week = 7;
    } else {
      day_week -= 1;
    }
    const isBetween = isWithinInterval(now, {
      start: startDate,
      end: endDate
    });
    if (
      isBetween
      /* && promo.quantity_remaining > 0 */
      && (promo.week_day === 0 || promo.week_day === day_week)
    ) {
      price = promo.discount_price.toFixed(2);
      offer.promo = promo;
    }
    return promo;
  });
  return {
    price: price,
    canShow: !!price
  };
};

const hasInstallments = (detail, totalPrice) => {
  const { siteInfo } = store.state;
  return siteInfo.payments_platform === 'stripe' && totalPrice > siteInfo.tda_settings.parceledMinimumAmount
    && !detail.unit_sales
    && (detail.reloadable || (detail.reservation && detail.inscription))
    && !detail.recurring
    && !store.getters.isImpersonateMode;
};

const computeCommonAttributes = details => ({
  first_purchase: details.first_purchase,
  horanet_product_code: details.horanet_product_code,
  horanet_product_reference: details.horanet_product_reference,
  inscription: details.inscription,
  pass: details.pass,
  product_image: details.product_image,
  recurring: details.recurring,
  reloadable: details.reloadable,
  reservation: details.reservation,
  unit_sales: details.unit_sales,
  stage: details.stage,
  stage1D: details.stage1D,
  license: details.license,
  product_name: details.product_name,
  product_uuid: details.product_uuid,
  product_type: details.product_type,
  offer_type: details.offer_type,
  session: details.session,
  isClubCard: details.isClubCard
});

const subscriptionPaymentInfo = (promoAmount, promoAmountFee, promoMetric) => {
  let firstPayment = 0;
  let recurringPayment = 0;
  let firstPaymentPromo = 0;
  let recurringPaymentPromo = 0;

  if (!store.state.cartItem || !store.state.cartItem.tariffs) {
    return { firstPayment: 0.00, recurringPayment: 0.00 };
  }
  const { promoCampaign } = store.state;
  let horanetPromo = null;

  const { tariffs } = store.state.cartItem;

  tariffs.forEach(tariff => {
    firstPayment += tariff.price;
    recurringPayment += tariff.price;

    if (computePromo(tariff).canShow) {
      firstPaymentPromo += parseInt(computePromo(tariff).price);
      recurringPaymentPromo += computePromo(tariff).price;
      horanetPromo = true;
    }

    if (promoCampaign) {
      const discount = promoCampaign.discount / 100;
      firstPaymentPromo += (tariff.price * discount);

      if (promoCampaign.recurring) {
        recurringPaymentPromo += (tariff.price * discount);
      }
    }
    if (tariff.options) {
      tariff.options.forEach(option => {
        firstPayment += option.price.price;
        if (option.detail && option.detail.recurring) {
          recurringPayment += option.price.price;
        }

        if (computePromo(tariff).canShow) {
          firstPaymentPromo += option.price.price;

          if (option.detail && option.detail.recurring) {
            recurringPaymentPromo += option.price.price;
          }
        }
      });
    }
  });

  if (horanetPromo) {
    return {
      firstPayment: (firstPayment / 100).toFixed(2),
      recurringPayment: (recurringPayment / 100).toFixed(2),
      hasPromo: true,
      hasRecurringPromo: true,
      promo: {
        firstPaymentPromo: (firstPaymentPromo / 100).toFixed(2),
        recurringPaymentPromo: (recurringPaymentPromo / 100).toFixed(2)
      }
    };
  }

  if (promoCampaign && promoCampaign.discount) {
    return {
      firstPayment: (firstPayment / 100).toFixed(2),
      recurringPayment: (recurringPayment / 100).toFixed(2),
      hasPromo: true,
      hasRecurringPromo: promoCampaign.recurring,
      promo: {
        firstPaymentPromo: ((firstPayment - firstPaymentPromo) / 100).toFixed(2),
        recurringPaymentPromo: ((recurringPayment - recurringPaymentPromo) / 100).toFixed(2)
      }
    };
  }

  if ((promoAmount || promoAmountFee) && promoMetric) {
    const { recurringPromo } = store.state;
    switch (promoMetric) {
      case 'euro': {
        const discount = parseFloat(promoAmount);
        const discountFee = parseFloat(promoAmountFee);
        if (discount > parseFloat(recurringPayment / 100) || discount < 0) {
          break;
        }
        if ((discount + discountFee) > parseFloat(firstPayment / 100) || (discount + discountFee) < 0) {
          break;
        }
        return {
          firstPayment: (firstPayment / 100).toFixed(2),
          recurringPayment: (recurringPayment / 100).toFixed(2),
          hasPromo: true,
          hasRecurringPromo: recurringPromo && discount,
          promo: {
            firstPaymentPromo: ((firstPayment / 100) - (discount || 0) - (discountFee || 0)).toFixed(2),
            recurringPaymentPromo: ((recurringPayment / 100) - (discount || 0)).toFixed(2)
          }
        };
      }
      case 'percentage': {
        const discount = parseFloat(promoAmount) / 100;
        if (discount > 1 || discount < 0) {
          break;
        }

        return {
          firstPayment: (firstPayment / 100).toFixed(2),
          recurringPayment: (recurringPayment / 100).toFixed(2),
          hasPromo: true,
          hasRecurringPromo: recurringPromo,
          promo: {
            firstPaymentPromo: parseFloat(((1 - discount) * (firstPayment / 100)).toFixed(2)),
            recurringPaymentPromo: parseFloat(((1 - discount) * (recurringPayment / 100)).toFixed(2))
          }
        };
      }
      default:
        break;
    }
  }

  return { firstPayment: (firstPayment / 100).toFixed(2), recurringPayment: (recurringPayment / 100).toFixed(2), hasPromo: false };
};

const computeSamePricePeriodOptions = (options, tariff) => {
  const validOptions = [];
  (options || []).map(option => {
    if (option.detail) {
      const samePeriodAndPrice = (option.detail.prices || []).find(price => price.tariffCategoryCode === tariff.tariffCategoryCode && price.period && price.period.uuid === tariff.period?.uuid);
      if (samePeriodAndPrice) {
        validOptions.push(option);
      }
    }
  });
  return validOptions;
};

const computeValidOrderPreReservations = async productIds => {
  const { orders, uuid } = store.state.user || {};
  const { offerInfo, siteInfo, sessionsCodes, products } = store.state;

  const validOrders = [];

  (orders || []).forEach(orderGeneric => {
    // Remove when implementing feature to Horanet Workspaces.
    if (!siteInfo.is_internal_session) {
      return;
    }

    if (!orderGeneric || orderGeneric.isPaid || orderGeneric.buyer?.uuid !== uuid || siteInfo.workspace !== orderGeneric.site_workspace || !orderGeneric.order || !orderGeneric.order.payment_link_url || orderGeneric.order.cancelledPreReservations) {
      return;
    }

    const intervalToCheck = siteInfo.is_internal_session ? { type: 'hours', value: 6 } : { type: 'minutes', value: 30 };

    const now = new Date();
    if (!isWithinInterval(new Date(parseFloat(orderGeneric.date)), { start: sub(now, { [intervalToCheck.type]: intervalToCheck.value }), end: now })) {
      return;
    }

    if (sessionsCodes?.length) {
      const orderSession = orderGeneric.order.order_items[0]?.session;

      if (!orderSession) {
        return;
      }

      let matchingSession = null;
      (products || []).forEach(prod => {
        if (!matchingSession) {
          matchingSession = (prod?.sessions || []).find(s => s.id === orderSession.code || (orderSession.recurring_uuid && s.recurring_uuid && s.recurring_uuid === orderSession.recurring_uuid));
        }
      });

      if (!matchingSession) {
        return;
      }

      validOrders.push({
        orderGeneric,
        session: {
          id: matchingSession.id,
          recurring: matchingSession.recurring,
          recurring_uuid: matchingSession.recurring_uuid,
          end_recurring_date: matchingSession.end_recurring_date,
          preReservedParticipants: matchingSession.pre_reserved_participants?.items || []
        }
      });
      return;
    }

    if (!productIds?.length || !offerInfo?.detail) {
      return;
    }

    const sameProduct = (orderGeneric.order.order_items || []).some(oi => productIds.includes(oi.product_uuid));

    if (!sameProduct) {
      return;
    }

    const offerWithReservations = offerInfo.detail && (offerInfo.detail.inscription || offerInfo.detail.reservation || offerInfo.detail.stage || offerInfo.detail.stage1D);

    if (!offerWithReservations) {
      return;
    }

    validOrders.push({ orderGeneric });
  });

  return validOrders.length ? validOrders.sort((a, b) => parseFloat(b.orderGeneric.date) - parseFloat(a.orderGeneric.date))[0] : null;
};

const validatePreReservationParticipants = async (participants, orderItems, preReservationSession) => {
  if (!participants?.length || !orderItems?.length || !preReservationSession) {
    return { matchCount: 0, allMatch: false };
  }

  const orderSession = orderItems[0]?.session;
  const { offerInfo } = store.state;

  if (!orderSession) {
    return { matchCount: 0, allMatch: false };
  }

  // Session matching logic from isSessionMatching
  const useRecurring = (offerInfo?.detail?.inscription || offerInfo?.detail?.stage) && orderSession?.recurring_uuid;

  let sessionMatches = false;
  if (useRecurring) {
    const orderRecurring = orderSession.recurring_uuid;
    const preReservationRecurring = preReservationSession.recurring_uuid;
    sessionMatches = orderRecurring && preReservationRecurring && orderRecurring === preReservationRecurring;
  } else {
    const orderCode = orderSession.code;
    const preReservationId = preReservationSession.id;
    sessionMatches = orderCode && preReservationId && orderCode === preReservationId;
  }

  if (!sessionMatches) {
    return { matchCount: 0, allMatch: false };
  }

  let preReservedParticipants = preReservationSession.preReservedParticipants || [];

  if (useRecurring && preReservationSession.recurring_uuid) {
    try {
      const recurringSessionsResponse = await getRecurringSessions({
        startDate: getUnixTime(startOfDay(new Date())),
        endDate: preReservationSession.end_recurring_date,
        workspace: store.state.siteInfo.workspace,
        recurringUuid: preReservationSession.recurring_uuid
      });

      if (!recurringSessionsResponse || !recurringSessionsResponse.success || !(recurringSessionsResponse.data || []).length) {
        return { matchCount: 0, allMatch: false };
      }

      const uniquePreReservedMap = new Map();
      (recurringSessionsResponse.data || []).forEach(session => {
        const sessionPreReservations = session.pre_reserved_participants?.items || session.preReservedParticipants || [];
        sessionPreReservations.forEach(p => {
          if (!uniquePreReservedMap.has(p.customer_uuid)) {
            uniquePreReservedMap.set(p.customer_uuid, p);
          }
        });
      });

      preReservedParticipants = Array.from(uniquePreReservedMap.values());
    } catch (err) {
      return { matchCount: 0, allMatch: false };
    }
  }

  // Check all participants and count matches
  let matchCount = 0;
  participants.forEach(participant => {
    const isPreReserved = preReservedParticipants.some(p => p.customer_uuid === participant.uuid);
    if (isPreReserved) {
      matchCount += 1;
    }
  });

  const allMatch = matchCount === participants.length;

  return { matchCount, allMatch };
};

const removeCartAndChangePage = async (page, productsIds = []) => {
  const shoppingCart = store.state.user?.shoppingCart;

  const sameOrderWithPreReservations = await computeValidOrderPreReservations(productsIds);

  if (sameOrderWithPreReservations) {
    store.commit('setPreReservationOrder', sameOrderWithPreReservations);
  }

  if (!shoppingCart || (shoppingCart && !shoppingCart.length)) {
    changeToPage(page);
    return;
  }

  const { workspace } = store.getters;
  const cartItem = shoppingCart.find(item => item.workspace === workspace);

  if (!cartItem) {
    changeToPage(page);
    return;
  }

  const { session } = cartItem.details[0];

  let alreadyInCart = productsIds.includes(cartItem.details[0].product_uuid);

  if (alreadyInCart && !store.getters.isEscapeGame) {
    alreadyInCart = `${store.state.offerInfo?.horanet_product?.horanet_product_code || store.state.offerInfo?.uuid}` === cartItem.details[0]?.horanet_product_code;
  }

  if (session && !session.available) {
    alreadyInCart = false;
  }

  if (!alreadyInCart) {
    await removeShoppingCart().then(res => {
      if (res) {
        store.commit('setUser', {
          ...store.state.user,
          shoppingCart: res
        });
      }
    });
    changeToPage(page);
    return;
  }

  if (sameOrderWithPreReservations) {
    store.commit('setPreReservationOrder', { ...sameOrderWithPreReservations, alreadyInCart });
  }
  store.commit('setPage', page);
  changeToPage('ResumeOrder');
};

const UNPAID_STATUS = 'unpaid';

/**
 * Maps a booking (from a contract or from a session's captain) into the
 * display-ready shape used by the unpaid bookings list.
 *
 * @param {Object} session The session the booking belongs to
 * @param {Object} booking The booking payload
 * @returns {{ key: string, sessionId: string, sessionName: string, orderUuid: string|null, redirectUrl: string|null }}
 */
const mapBookingToUnpaidEntry = (session, booking) => ({
  key: `${session.id}-${booking.orderGenericUuid || booking.sgId}`,
  sessionId: session.id,
  sessionName: session.name,
  orderUuid: booking.orderGenericUuid || null,
  redirectUrl: booking.sharegroopOrder?.redirectUrl || null
});

/**
 * Flattens the /api/amplify/unpaidBookings response into a display-ready list.
 * Each entry maps to one unpaid booking and carries the Sharegroop regularisation
 * link when the backend managed to resolve it.
 *
 * A session's `captain` (the participant who created the reservation) is checked
 * separately from `contracts`: it is only counted when it belongs to the current
 * customer and is itself unpaid, otherwise another participant's unpaid booking
 * could incorrectly surface on this customer's list.
 *
 * @param {Array} sessions Sessions returned by getCustomerAmplifyUnpaidBookings
 * @returns {Array<{ key: string, sessionId: string, sessionName: string, orderUuid: string, redirectUrl: string|null }>}
 */
const buildUnpaidBookings = sessions => {
  if (!Array.isArray(sessions)) return [];

  const customer = store.state.user;

  const unpaid = sessions.flatMap(session => {
    const bookings = (session.contracts || [])
      .filter(({ booking }) => booking && booking.status === UNPAID_STATUS)
      .map(({ booking }) => mapBookingToUnpaidEntry(session, booking));

    if (session?.captain?.horanetId === customer?.horanet_id && session?.captain?.booking && session.captain.booking.horanetId === customer?.horanet_id && session.captain.booking.status === UNPAID_STATUS) {
      bookings.push(mapBookingToUnpaidEntry(session, session.captain.booking));
    }

    return bookings;
  });

  return unpaid;
};

/**
 * Resolves the unpaid invoices of each unpaid subscription. A subscription can carry several
 * unpaid invoices, so they stay grouped under their subscription instead of being flattened.
 * Subscriptions without any resolvable invoice link are dropped.
 *
 * @param {Array} subscriptions Sharegroop subscriptions already filtered on the unpaid status
 * @returns {Promise<Array<{ key: string, name: string|null, orderUuid: string, invoices: Array<{ key: string, reference: number, redirectUrl: string }> }>>}
 */
const buildUnpaidSubscriptions = async subscriptions => {
  const results = await Promise.all((subscriptions || []).map(async subscription => {
    const orderUuid = subscription.metadata?.orderUuid || subscription.trackId || null;

    if (!orderUuid) return { success: true, subscription: null };

    const response = await getUnpaidInvoices(orderUuid);

    if (response?.success !== true) return { success: false, subscription: null };

    const invoices = (response.invoices || [])
      .filter(invoice => invoice?.url)
      .map((invoice, index) => ({
        key: `${subscription.id}-${index}`,
        reference: index + 1,
        redirectUrl: invoice.url
      }));

    if (!invoices.length) return { success: true, subscription: null };

    return {
      success: true,
      subscription: {
        key: subscription.id,
        name: subscription.items?.[0]?.name || null,
        orderUuid,
        invoices
      }
    };
  }));

  return {
    success: results.every(result => result.success),
    subscriptions: results.map(result => result.subscription).filter(Boolean)
  };
};

/**
 * Resolves whether the customer may enter the funnel.
 *
 * Fails closed: when any of the unpaid lookups cannot be completed (timeout, network or upstream
 * error) the result carries `success: false` and the caller must block access rather than assume
 * the customer is clear.
 *
 * @param {string} workspace Site workspace, used to scope the subscription lookup
 * @returns {Promise<{ success: boolean, hasUnpaid: boolean, unpaidBookings: Array, unpaidSubscriptions: Array }>}
 */
const checkCustomerUnpaidStatus = async workspace => {
  const checkFailed = { success: false, hasUnpaid: false, unpaidBookings: [], unpaidSubscriptions: [] };

  const [bookingsResponse, subscriptionsResponse] = await Promise.all([
    getCustomerAmplifyUnpaidBookings(),
    getParticipantActiveSubscriptionsInfo(workspace)
  ]);

  if (bookingsResponse?.success !== true) return checkFailed;

  /* This endpoint returns the payload as-is on success, and only sets `success` when it fails. */
  if (!subscriptionsResponse || subscriptionsResponse.success === false) return checkFailed;

  const unpaidBookings = buildUnpaidBookings(bookingsResponse.sessions);

  const unpaidSharegroopSubscriptions = (subscriptionsResponse.sharegroopCustomer?.subscriptions || [])
    .filter(subscription => subscription.status === UNPAID_STATUS);

  const { success, subscriptions } = await buildUnpaidSubscriptions(unpaidSharegroopSubscriptions);

  if (!success) return checkFailed;

  return {
    success: true,
    hasUnpaid: !!unpaidBookings.length || !!subscriptions.length,
    unpaidBookings,
    unpaidSubscriptions: subscriptions
  };
};

export {
  checkCustomerUnpaidStatus,
  handleOfferInfo,
  handleOptionFlow,
  handleEscapeProduct,
  handleProduct,
  handleTariff,
  injectCurrentParticipant,
  changeToPage,
  setOptions,
  getRequiredDocuments,
  hasProductsStep,
  hasTerrainsStep,
  confirmParticipants,
  initializeCartItem,
  computeAvailableOptions,
  computeTariffOptions,
  computePromo,
  hasInstallments,
  computeCommonAttributes,
  subscriptionPaymentInfo,
  computeSamePricePeriodOptions,
  validatePreReservationParticipants,
  removeCartAndChangePage
};
